import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { customerMaintenanceInitializer } from './services/maintenance/customer-maintenance.initializer';
import { FeatureSettingsService } from './services/feature-settings/feature-settings.service';
import { FidelityVoucherPublicAccessComponent } from './pages/fidelity/fidelity-customer-details/fidelity-vouchers/fidelity-voucher-public-access/fidelity-voucher-public-access.component';
import { QRCodeModule } from 'angularx-qrcode';
import { KitchenComponent } from './pages/kitchen/kitchen.component';
import { KitchenModelComponent } from './pages/kitchen/model/model.component';
import { KitchenLayoutComponent } from './pages/kitchen/layout/layout.component';
import { KitchenOvenComponent } from './pages/kitchen/oven/oven.component';
import { KitchenFridgeComponent } from './pages/kitchen/fridge/fridge.component';
import { KitchenRecapComponent } from './pages/kitchen/recap/recap.component';
import { KitchenStyleComponent } from './pages/kitchen/style/style.component';
import { ModalsComponent } from './modals/modals.component';
import { HomeComponent } from './pages/home/home.component';
import { HeaderComponent } from './ui/header/header.component';
import { CommunicationMediaGalleryComponent } from './ui/communication-media-gallery/communication-media-gallery.component';
import { CommunicationAlertDialogComponent } from './ui/communication-alert-dialog/communication-alert-dialog.component';
import { DatasService } from './services/datas.service';
import { CurrentService } from './services/current.service';
import { BtnComponent } from './ui/btn/btn.component';
import { BreadcrumbComponent } from './ui/breadcrumb/breadcrumb.component';
import { LoginComponent } from './modals/login/login.component';
import { ModalService } from './services/modal.service';
import { RegisterComponent } from './modals/register/register.component';
import { ConfigurationComponent } from './pages/configuration/configuration.component';
import { ProjectsComponent } from './modals/projects/projects.component';
import { PriceInfosComponent } from './modals/price-infos/price-infos.component';
import { ProjectComponent } from './pages/project/project.component';
import { ProductsListComponent } from './modals/products-list/products-list.component';
import { DebugInfosComponent } from './modals/debug-infos/debug-infos.component';

import { CommonModule, registerLocaleData } from '@angular/common';

import { SliderModule } from 'primeng/slider';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TriStateCheckboxModule } from 'primeng/tristatecheckbox';
import { TooltipModule } from 'primeng/tooltip';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { SkeletonModule } from 'primeng/skeleton';
import { CalendarModule } from 'primeng/calendar';

import localeFr from '@angular/common/locales/fr';
import { Nl2BrPipe } from './services/pipes/nl2br.pipe';
import { environment } from '../environments/environment';
import { PrintMediasComponent } from './modals/print-medias/print-medias.component';
import { ImageZoomComponent } from './modals/image-zoom/image-zoom.component';
import { SafeHtmlPipe, SafePipe, SafeStylePipe } from './services/pipes/safe.pipe';
import { LogoutComponent } from './modals/logout/logout.component';
import { LoaderComponent } from './ui/loader/loader.component';
import { ContactComponent } from './modals/contact/contact.component';
import { EditableTextComponent } from './ui/editable-text/editable-text.component';
import { ProjectDuplicateComponent } from './modals/project-duplicate/project-duplicate.component';
import { ProjectDeleteComponent } from './modals/project-delete/project-delete.component';
import { StorageComponent } from './pages/storage/storage.component';
import { StorageBoxComponent } from './pages/storage/inspiration/box/box.component';
import { StorageFrontComponent } from './pages/storage/inspiration/front/front.component';
import { StorageLegsComponent } from './pages/storage/inspiration/legs/legs.component';
import { StorageHandlesComponent } from './pages/storage/inspiration/handles/handles.component';
import { StorageRecapComponent } from './pages/storage/inspiration/recap/recap.component';
import { InspirationTipComponent } from './modals/inspiration-tip/inspiration-tip.component';
import { ModalMessageComponent } from './modals/message/message.component';
import { ServiceManager } from './services/configurators-2d/service-manager';
import { Configurator2DSelectionComponent } from './pages/configurators-2d/selector/configurators-2d-selection.component';
import { Configurators2DComponent } from './pages/configurators-2d/configurators-2d.component';
import { ClosetDoorsStandardComponent } from './pages/configurators-2d/closet-doors/standard/closet-doors-standard.component';
import { ClosetDoorsStandardPreviewComponent } from './pages/configurators-2d/closet-doors/standard/preview/preview.component';
import { ClosetDoorsStandardEnvironmentComponent } from './pages/configurators-2d/closet-doors/standard/environment/environment.component';
import { ClosetDoorsQuoteComponent } from './modals/closet-doors-quote/closet-doors-quote.component';
import { ClosetDoorsStandardDoorConfigurationComponent } from './pages/configurators-2d/closet-doors/standard/door-configuration/door-configuration.component';
import { ClosetDoorsStandardDoorRecessComponent } from './pages/configurators-2d/closet-doors/standard/door-configuration/sub-steps/recess/recess.component';
import { ClosetDoorsStandardDoorPlinthComponent } from './pages/configurators-2d/closet-doors/standard/door-configuration/sub-steps/plinth/plinth.component';
import { ClosetDoorsStandardDoorNumberComponent } from './pages/configurators-2d/closet-doors/standard/door-configuration/sub-steps/door-number/door-number.component';
import { ClosetDoorsStandardFinishesComponent } from './pages/configurators-2d/closet-doors/standard/finishes/finishes.component';
import { ClosetDoorsStandardFinishesDoorsComponent } from './pages/configurators-2d/closet-doors/standard/finishes/sub-steps/doors/doors.component';
import { ClosetDoorsStandardFinishesColorsComponent } from './pages/configurators-2d/closet-doors/standard/finishes/sub-steps/colors/colors.component';
import { ClosetDoorsStandardFinishesFramesComponent } from './pages/configurators-2d/closet-doors/standard/finishes/sub-steps/frames/frames.component';
import { ClosetDoorsStandardRangeComponent } from './pages/configurators-2d/closet-doors/standard/range/range.component';
import { ClosetDoorsStandardSummaryComponent } from './pages/configurators-2d/closet-doors/standard/summary/summary.component';
import { PreviewStepEnvironmentComponent } from './pages/configurators-2d/closet-doors/standard/preview/steps/environment/preview-step-environment.component';
import { PreviewStepDoorConfigurationComponent } from './pages/configurators-2d/closet-doors/standard/preview/steps/door-configuration/preview-step-door-configuration.component';
import { PreviewStepRangeComponent } from './pages/configurators-2d/closet-doors/standard/preview/steps/range/preview-step-range.component';
import { PreviewStepFinishesComponent } from './pages/configurators-2d/closet-doors/standard/preview/steps/finishes/preview-step-finishes.component';
import { PreviewStepSummaryComponent } from './pages/configurators-2d/closet-doors/standard/preview/steps/summary/preview-step-summary.component';
import { ClosetDoorsPercentageComponent } from './modals/closet-doors-percentage/closet-doors-percentage.component';
import { ClosetDoorsStructureInfoComponent } from './modals/closet-doors-structure-info/closet-doors-structure-info.component';
import { ClosetDoorsPlinthInfoComponent } from './modals/closet-doors-plinth-infos/closet-doors-plinth-info.component';
import { ClosetDoorsCustomComponent } from './pages/configurators-2d/closet-doors/custom/closet-doors-custom.component';
import { ClosetDoorsCustomPreviewComponent } from './pages/configurators-2d/closet-doors/custom/preview/preview.component';
import { ClosetDoorsCustomEnvironmentComponent } from './pages/configurators-2d/closet-doors/custom/environment/environment.component';
import { ClosetDoorsCustomClosetConfigurationComponent } from './pages/configurators-2d/closet-doors/custom/closet-configuration/closet-configuration.component';
import { ClosetDoorsCustomSuppliersComponent } from './pages/configurators-2d/closet-doors/custom/suppliers/suppliers.component';
import { ClosetDoorsCustomFinishesComponent } from './pages/configurators-2d/closet-doors/custom/finishes/finishes.component';
import { ClosetDoorsCustomAccessoriesComponent } from './pages/configurators-2d/closet-doors/custom/accessories/accessories.component';
import { ClosetDoorsCustomSummaryComponent } from './pages/configurators-2d/closet-doors/custom/summary/summary.component';
import { ClosetDoorsAdministrationComponent } from './pages/configurators-2d/closet-doors/standard/administration/administration.component';
import { Configurators2DAdministrationComponent } from './pages/configurators-2d/administration/configurators-2d-administration.component';
import { FenceAdministrationComponent } from './pages/configurators-2d/fence/administration/administration.component';
import { DeckingBoardsAdministrationComponent } from './pages/configurators-2d/decking-boards/administration/administration.component';
import { FenceComponent } from './pages/configurators-2d/fence/fence.component';
import { FenceEnvironmentComponent } from './pages/configurators-2d/fence/environment/environment.component';
import { FenceConfigurationComponent } from './pages/configurators-2d/fence/configuration/configuration.component';
import { FenceAccessoriesComponent } from './pages/configurators-2d/fence/accessories/accessories.component';
import { FenceSummaryComponent } from './pages/configurators-2d/fence/summary/summary.component';
import { BaseAdministrationComponent } from './pages/configurators-2d/administration/base-administration/base-administration.component';
import { AdministratorsTabComponent } from './pages/configurators-2d/administration/administrators-tab/administrators-tab.component';
import { PricesTabComponent } from './pages/configurators-2d/administration/prices-tab/prices-tab.component';
import { PricesClosetDoorsCustomTabComponent } from './pages/configurators-2d/administration/prices-closet-doors-custom-tab/prices-closet-doors-custom-tab.component';
import { PicturesTabComponent } from './pages/configurators-2d/administration/pictures-tab/pictures-tab.component';
import { PicturesClosetDoorsCustomTabComponent } from './pages/configurators-2d/administration/pictures-closet-doors-custom-tab/pictures-closet-doors-custom-tab.component';
import { StatisticsTabComponent } from './pages/configurators-2d/administration/statistics-tab/statistics-tab.component';
import { FeatureSettingsTabComponent } from './pages/configurators-2d/administration/feature-settings-tab/feature-settings-tab.component';
import { FeatureSettingsAdministrationComponent } from './pages/configurators-2d/administration/feature-settings-tab/feature-settings-administration.component';
import { DiscountsTabComponent } from './pages/configurators-2d/administration/discounts-tab/discounts-tab.component';
import { ClosetDoorsAdministrationMessageComponent } from './modals/closet-doors-administration-message/closet-doors-administration-message.component';
import { OrderProductsComponent } from './modals/order-products/order-products.component';
import { InstallationQuoteComponent } from './modals/installation-quote/installation-quote.component';
import { ConvertDistancePipe } from './services/pipes/convert-distance-pipe';
import { ImageLoaderComponent } from './ui/loader/image-loader.component';
import { GenericErrorComponent } from './modals/generic-error/generic-error.component';
import { AdministrationAdministratorCreateComponent } from './modals/administration-administrator-create/administration-administrator-create.component';
import { AdministrationAdministratorUpdateComponent } from './modals/administration-administrator-update/administration-administrator-update.component';
import { AdministrationDiscountDeleteComponent } from './modals/administration-discount-delete/administration-discount-delete.component';
import { DeckingBoardsPreviewComponent } from './pages/configurators-2d/decking-boards/preview/preview.component';
import { DeckingBoardsEnvironmentComponent } from './pages/configurators-2d/decking-boards/environment/environment.component';
import { DeckingBoardsAccessoriesComponent } from './pages/configurators-2d/decking-boards/accessories/accessories.component';
import { DeckingBoardsConfigurationComponent } from './pages/configurators-2d/decking-boards/decking-boards-configuration/decking-boards-configuration.component';
import { DeckingBoardsFinishesComponent } from './pages/configurators-2d/decking-boards/finishes/finishes.component';
import { DeckingBoardsSummaryComponent } from './pages/configurators-2d/decking-boards/summary/summary.component';
import { DeckingBoardsComponent } from './pages/configurators-2d/decking-boards/decking-boards.component';
import { DeckingBoardsClassInfoComponent } from './modals/decking-boards-class-info/decking-boards-class-info.component';
import { configuratorProviders } from './providers/configurators-2d.providers';
import { closetDoorsCustomProviders } from './providers/closet-doors-custom.provider';
import { fenceProviders } from './providers/fence.providers';
import { deckingBoardsProviders } from './providers/decking-boards.providers';
import { FenceInfosAngleKitComponent } from './modals/fence-infos-angle-kit/fence-infos-angle-kit.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AlertModule } from './_alert';
import { FenceInfosBasePlateComponent } from './modals/fence-infos-base-plate/fence-infos-base-plate.component';
import { GenericInfoComponent } from './modals/generic-info/generic-info.component';
import { GTM } from './services/gtm.service';
import { CarpentryComponent } from './pages/configurators-2d/carpentry/carpentry.component';
import { CarpentryConfigurationComponent } from './pages/configurators-2d/carpentry/configuration/configuration.component';
import { CarpentrySummaryComponent } from './pages/configurators-2d/carpentry/summary/summary.component';
import { ShoppingCartProductDeleteComponent } from './modals/shopping-cart-product-delete/shopping-cart-product-delete.component';
import { ShoppingCartComponent } from './pages/shopping-cart/shopping-cart.component';
import { ShoppingCartQuantityUpdateComponent } from './modals/shopping-cart-quantity-update/shopping-cart-quantity-update.component';
import { ShoppingCartInformationsComponent } from './modals/shopping-cart-informations/shopping-cart-informations.component';
import { ShoppingCartSupplierSendingComponent } from './modals/shopping-cart-supplier-sending/shopping-cart-supplier-sending.component';
import { ShoppingCartOrderNumberEditionComponent } from './modals/shopping-cart-order-number-edition/shopping-cart-order-number-edition.component';
import { ShoppingCartAddAssistanceComponent } from './modals/shopping-cart-add-assistance/shopping-cart-add-assistance.component';
import { ShoppingCartCheckOrderComponent } from './modals/shopping-cart-check-order/shopping-cart-check-order.component';
import { ShoppingCartVatUpdateComponent } from './modals/shopping-cart-vat-update/shopping-cart-vat-update.component';
import { ShoppingCartInstallationReminderComponent } from './modals/shopping-cart-installation-reminder/shopping-cart-installation-reminder.component';
import { CarpentryAccessoriesComponent } from './pages/configurators-2d/carpentry/accessories/accessories.component';
import { MultiVatQuoteComponent } from './modals/multi-vat-quote/multi-vat-quote.component';
import { CdmCarpentryTabComponent } from './pages/configurators-2d/administration/cdm_carpentry-tab/cdm-carpentry-tab.component';
import { DiscountCarpentryTabComponent } from './pages/configurators-2d/administration/discount-carpentry-tab/discount-carpentry-tab.component';
import { AccessoryCarpentryTabComponent } from './pages/configurators-2d/administration/accessory-carpentry-tab/accessory-carpentry-tab.component';
import { QuotationDisabledProductsAlertComponent } from './pages/configurators-2d/quotation/quotation-disabled-products-alert/quotation-disabled-products-alert.component';
import { WorkbenchPreviewModule } from './pages/configurators-2d/workbench/preview/workbench-preview.module';
import { WorkbenchPlanGroupsComponent } from './pages/configurators-2d/workbench/plan-groups/plan-groups.component';
import { WorkbenchPlanSectionsComponent } from './pages/configurators-2d/workbench/plan-groups/plan-sections/plan-sections.component';
import { WorkbenchMaterialsColorsComponent } from './pages/configurators-2d/workbench/materials-colors/materials-colors.component';
import { WorkbenchFinishesComponent } from './pages/configurators-2d/workbench/finishes/finishes.component';
import { WorkbenchJambsInfosComponent } from './pages/configurators-2d/workbench/finishes/jambs-infos/jambs-infos.component';
import { WorkbenchSummaryComponent } from './pages/configurators-2d/workbench/summary/summary.component';
import { WorkbenchComponent } from './pages/configurators-2d/workbench/workbench.components';
import { WorkbenchSplashbacksComponent } from './pages/configurators-2d/workbench/splashbacks/splashbacks.component';
import { WorkbenchColorGroupsPlansComponent } from './pages/configurators-2d/workbench/materials-colors/color-groups-plans/color-groups-plans.component';
import { WorkbenchPlanCuttingInfosComponent } from './pages/configurators-2d/workbench/finishes/plan-cutting-infos/plan-cutting-infos.component';
import { WorkbenchSplashbackCuttingInfosComponent } from './pages/configurators-2d/workbench/splashbacks/splashback-cutting-infos/splashback-cutting-infos.component';
import { AppointmentLinkComponent } from './modals/appointment-link/appointment-link.component';
import { HbmApiService } from './services/hbm/hbm-api.service';
import { kitchensProviders } from './providers/kitchen.providers';
import { SummaryComponent } from './pages/summary/summary.component';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CarouselModule } from 'primeng/carousel';
import { PanelModule } from 'primeng/panel';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DialogModule } from 'primeng/dialog';
import { MessagesModule } from 'primeng/messages';
import { FlexibleDatePickerComponent } from './ui/flexible-date-picker/flexible-date-picker.component';
import { HbmIframeService } from './services/hbm/hbm-iframe.service';
import { SidebarModule } from 'primeng/sidebar';
import { FullCalendarModule } from '@fullcalendar/angular';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';

import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CircleBtnComponent } from './ui/circle-btn/circle-btn.component';
import { ToastService } from './services/toast.service';
import { KitchenIdentificationComponent } from './pages/kitchen-identification/kitchen-identification.component';
import { IdentificationComponent } from './ui/identification/identification.component';
import { SearchIdentificationComponent } from './ui/search-identification/search-identification.component';
import { FidelityInfoComponent } from './ui/fidelity-info/fidelity-info.component';
import { OfferCouponCardComponent } from './ui/offer-coupon-card/offer-coupon-card.component';
import { FidelityCardTypePipe } from './services/fidelity/fidelity';
import { CardTypeChoiceComponent } from './modals/register/card-type-choice/card-type-choice.component';
import { CustomerTypeChoiceComponent } from './modals/register/customer-type-choice/customer-type-choice.component';
import { FidelityPaymentQRCodeComponent } from './modals/fidelity-payment-qrcode/fidelity-payment-qrcode.component';
import { KitchenValidationComponentv1 } from './pages/validation/validation.component';
import { KitchenValidationProjectInfoComponent } from './pages/validation/ui/validation-project-info/validation-project-info.component';
import { FidelityCardValidationComponent } from './ui/vendor-validation/fidelity-card-validation/fidelity-card-validation.component';
import { KitchenValidationProjectResumeComponent } from './pages/validation/ui/validation-project-resume/validation-project-resume.component';
import { KitchenValidationSideBarActionsComponent } from './pages/validation/ui/validation-side-bar-actions/validation-side-bar-actions.component';
import { KitchenValidationProjectResumeTableComponent } from './pages/validation/ui/validation-project-resume/validation-project-resume-table/validation-project-resume-table.component';
import { FidelityAdvantageComponent } from './ui/vendor-validation/fidelity-advantage/fidelity-advantage.component';
import { VendorValidationComponent } from './ui/vendor-validation/validation.component';
import { ValidationProjectInfoComponent } from './ui/vendor-validation/validation-project-info/validation-project-info.component';
import { ValidationProjectResumeComponent } from './ui/vendor-validation/validation-project-resume/validation-project-resume.component';
import { ValidationProjectResumeTableComponent } from './ui/vendor-validation/validation-project-resume/validation-project-resume-table/validation-project-resume-table.component';
import { ValidationSideBarActionsComponent } from './ui/vendor-validation/validation-side-bar-actions/validation-side-bar-actions.component';
import { storagesProviders } from './providers/storage.providers';
import { BaseInstallationComponent } from './pages/tunnel/common/base-installation/base-installation.component';
import { ButtonComponent } from './ui/button/button.component';
import { ActionBarComponent } from './ui/action-bar/action-bar.component';
import { CardMarketingComponent } from './ui/card-marketing/card-marketing.component';
import { SelectionCardComponent } from './ui/selection-card/selection-card.component';
import { TunnelComponent } from './pages/tunnel/tunnel.component';
import { TunnelBreadcrumbComponent } from './ui/tunnel-breadcrumb/tunnel-breadcrumb.component';
import { SearchBarComponent } from './ui/search-bar/search-bar.component';
import { BaseFundingComponent } from './pages/tunnel/common/base-funding/base-funding.component';
import { BaseValidationComponent } from './pages/tunnel/common/base-validation/base-validation.component';
import { TutorialOverlayComponent } from './ui/tutorial-overlay/tutorial-overlay.component';
import { PortalPlaceholderComponent } from './ui/portal-placeholder/portal-placeholder.component';
import { CustomerInfoComponent } from './ui/customer-info/customer-info.component';
import { CustomerInfoFidelityComponent } from './ui/customer-info/customer-info-fidelity/customer-info-fidelity.component';
import { CustomerInfoDetailsComponent } from './ui/customer-info/customer-info-details/customer-info-details.component';
import { ProjectInfoComponent } from './ui/project-info/project-info.component';
import { KitchenInstallationComponent } from './pages/tunnel/kitchen/kitchen-installation/kitchen-installation.component';
import { KitchenFundingComponent } from './pages/tunnel/kitchen/kitchen-funding/kitchen-funding.component';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { DropdownModule } from 'primeng/dropdown';
import { AccordionModule } from 'primeng/accordion';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TabMenuModule } from 'primeng/tabmenu';
import { StorageVendorInspirationComponent } from './pages/storage-vendor/storage-vendor-inspiration/storage-vendor-inspiration.component';
import { StorageVendorBaseComponent } from './pages/storage-vendor/storage-vendor-base/storage-vendor-base.component';
import { KitchenTunnelValidationComponent } from './pages/tunnel/kitchen/kitchen-tunnel-validation/kitchen-tunnel-validation.component';
import { CollapsibleSectionComponent } from './ui/collapsible-section/collapsible-section.component';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { StorageInstallationComponent } from './pages/tunnel/storage/storage-installation/storage-installation.component';
import { BathroomHbmInstallationComponent } from './pages/tunnel/bathroom-hbm/bathroom-hbm-installation/bathroom-hbm-installation.component';
import { BathroomHbmTunnelValidationComponent } from './pages/tunnel/bathroom-hbm/bathroom-hbm-tunnel-validation/bathroom-hbm-tunnel-validation.component';
import { ModalWarningInspirationComponent } from './modals/warning-inspiration/warning-inspiration.component';
import { StorageFundingComponent } from './pages/tunnel/storage/storage-funding/storage-funding.component';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MarketingConsentSelectorComponent } from './ui/marketing-consent-selector/marketing-consent-selector.component';
import { StorageTunnelValidationComponent } from './pages/tunnel/storage/storage-tunnel-validation/storage-tunnel-validation.component';
import { StorageRoomComponent } from './pages/storage/inspiration/room/room.component';
import { FreeCardCouponRewardsPipe } from './services/fidelity/freeCardCouponRewardsPipe';
import { AppointmentComponent } from './pages/appointment/appointment.component';
import { PortalPlanningComponent } from './ui/portal-planning/portal-planning.component';
import { AppointmentIdentificationComponent } from './pages/appointment/appointment-identification/appointment-identification.component';
import { AppointmentCalendarComponent } from './pages/appointment/appointment-calendar/appointment-calendar.component';
import { CustomerInfoAppointmentComponent } from './ui/customer-info/customer-info-appointment/customer-info-appointment.component';
import { HelpActionComponent } from './ui/help-action/help-action.component';
import { ErrorPanelComponent, GlobalErrorHandler, globalErrorInterceptor } from '@simphonis/ngx-error-panel';
import { BaseComplementaryProductComponent } from './pages/tunnel/common/base-complementary-product/base-complementary-product.component';

import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { CastoLazyImageDirective } from 'src/app/services/directives/casto-lazy-image-directive';
import { GenericErrorCustomerComponent } from './modals/generic-error-customer/generic-error-customer.component';
import { TreeModule } from 'primeng/tree';
import { ComplementaryProductCardComponent } from 'src/app/pages/tunnel/common/base-complementary-product/complementary-product-card/complementary-product-card.component';
import { ComplementaryProductListComponent } from 'src/app/pages/tunnel/common/base-complementary-product/complementary-product-list/complementary-product-list.component';
import { ComplementaryProductFilterComponent } from 'src/app/pages/tunnel/common/base-complementary-product/complementary-product-filter/complementary-product-filter.component';
import { ComplementaryProductChipsComponent } from 'src/app/pages/tunnel/common/base-complementary-product/complementary-product-chips/complementary-product-chips.component';
import { ChipModule } from 'primeng/chip';
import { FencePreviewModule } from './pages/configurators-2d/fence/preview/fence-preview.module';
import { FenceMaterialsModule } from './pages/configurators-2d/fence/materials/fence-materials.module';
import { TooltipDirective } from './services/directives/tooltip-directive';
import { FeatureDiscoveryDirective } from './services/directives/feature-discovery.directive';
import { GenericConfirmComponent } from './modals/generic-confirm/generic-confirm.component';
import { BasketConfirmationComponent } from './modals/basket-confirmation/basket-confirmation.component';
import { EndingPageComponent } from './pages/ending-page/ending-page.component';
import { ProductDetailInfosListComponent } from './pages/product-detail/product-detail-infos-list/product-detail-infos-list.component';
import { ProductDetailInfosSectionComponent } from './pages/product-detail/product-detail-infos-section/product-detail-infos-section.component';
import { ProductDetailStarRatingComponent } from './pages/product-detail/product-detail-star-rating/product-detail-star-rating.component';
import { ProductDetailComponent } from './pages/product-detail/product-detail.component';
import { ProductItemComponent } from './pages/product-list/product-item/product-item.component';
import { ProductListComponent } from './pages/product-list/product-list.component';
import { PurchaseListItemComponent } from './pages/purchase-list/purchase-list-item/purchase-list-item.component';
import { PurchaseListComponent } from './pages/purchase-list/purchase-list.component';
import { RegisterCustomerMobileComponent } from './pages/register-customer-mobile/register-customer-mobile.component';
import { SearchCustomerMobileComponent } from './pages/search-customer-mobile/search-customer-mobile.component';
import { ActionButtonComponent } from './ui/action-button/action-button.component';
import { IdentificationResultCardListItemComponent } from './ui/identification/identification-result-card-list/identification-result-card-list-item/identification-result-card-list-item.component';
import { IdentificationResultCardListComponent } from './ui/identification/identification-result-card-list/identification-result-card-list.component';
import { QuantityInputComponent } from './ui/quantity-input/quantity-input.component';
import { PaginatorModule } from 'primeng/paginator';
import { CustomerInfoFormComponent } from './modals/customer-info-form/customer-info-form.component';
import { CiamProfileCompletionComponent } from './modals/ciam-profile-completion/ciam-profile-completion.component';
import { ConfigSentScreenComponent } from './ui/config-sent-screen/config-sent-screen.component';
import { LightBoxCarouselComponent } from './ui/light-box-carousel/light-box-carousel.component';

import { BathroomTunnelProductsComponent } from './pages/tunnel/bathroom/bathroom-tunnel-products/bathroom-tunnel-products.component';
import { BasketTunnelProductsComponent } from './pages/tunnel/basket/basket-tunnel-products/basket-tunnel-products.component';
import { TunnelProductSearchComponent } from './pages/tunnel/common/purchase-list/tunnel-product-search/tunnel-product-search.component';
import { TunnelProductListComponent } from './pages/tunnel/common/purchase-list/tunnel-product-search/tunnel-product-list/tunnel-product-list.component';
import { TunnelProductCardComponent } from './pages/tunnel/common/purchase-list/tunnel-product-search/tunnel-product-list/tunnel-product-card/tunnel-product-card.component';
import { TunnelCartPanelComponent } from './pages/tunnel/common/purchase-list/tunnel-cart-panel/tunnel-cart-panel.component';
import { TunnelCartListComponent } from './pages/tunnel/common/purchase-list/tunnel-cart-panel/tunnel-cart-list/tunnel-cart-list.component';
import { TunnelCartItemComponent } from './pages/tunnel/common/purchase-list/tunnel-cart-panel/tunnel-cart-list/tunnel-cart-item/tunnel-cart-item.component';
import { BasketTunnelValidationComponent } from './pages/tunnel/basket/basket-tunnel-validation/basket-tunnel-validation.component';
import { BathroomTunnelInstallationComponent } from './pages/tunnel/bathroom/bathroom-tunnel-installation/bathroom-tunnel-installation.component';
import { BathroomTunnelFundingComponent } from './pages/tunnel/bathroom/bathroom-tunnel-funding/bathroom-tunnel-funding.component';
import { BathroomTunnelConceptionComponent } from './pages/tunnel/bathroom/bathroom-tunnel-conception/bathroom-tunnel-conception.component';
import { BathroomTunnelConceptionSummaryComponent } from './pages/tunnel/bathroom/bathroom-tunnel-conception/bathroom-tunnel-conception-summary.component';
import { BathroomConfigurationPreviewComponent } from './pages/tunnel/bathroom/bathroom-configuration-preview/bathroom-configuration-preview.component';
import { DesktopConfiguratorTemplateComponent } from './pages/tunnel/common/desktop-configurator-template/desktop-configurator-template.component';
import { BathroomConfiguratorChoiceComponent } from './pages/bathroom-configurator-choice/bathroom-configurator-choice.component';
import { BathroomRangeSelectorComponent } from './pages/configurators-2d/mobile/bathroom/bathroom-range-selector/bathroom-range-selector.component';
import { CompositionComponent } from './pages/configurators-2d/mobile/bathroom/composition/composition.component';
import { CompositionFiltersComponent } from './pages/configurators-2d/mobile/bathroom/composition/composition-filters/composition-filters.component';
import { BathroomTunnelValidationComponent } from './pages/tunnel/bathroom/bathroom-tunnel-validation/bathroom-tunnel-validation.component';
import { BathroomConfigurationService } from './services/configurators-2d/mobile/bathroom/bathroom-configuration.service';
import { BathroomValidationService } from './services/configurators-2d/mobile/bathroom/bathroom-validation.service';
import { BathroomConfigurationComponent } from './pages/configurators-2d/mobile/bathroom/bathroom-configuration/bathroom-configuration.component';
import { BathroomRenderComponent } from './pages/configurators-2d/mobile/bathroom/bathroom-configuration/bathroom-render/bathroom-render.component';
import { BathroomConfigurationEndingComponent } from './pages/configurators-2d/mobile/bathroom/bathroom-configuration/bathroom-configuration-ending/bathroom-configuration-ending.component';
import { BathroomEndingService } from './services/configurators-2d/mobile/bathroom/bathroom-ending.service';
import { bathroomProviders } from './providers/bathroom.providers';
import { basketProviders } from './providers/basket.providers';
import { bathroomHbmProviders } from './providers/bathroom-hbm.providers';
import { UNSAVED_CHANGES_CHECKERS } from './services/interfaces/has-unsaved-changes.interface';
import { UpdateCustomerMobileComponent } from './pages/update-customer-mobile/update-customer-mobile.component';
import { CarpentryTunnelProductsComponent } from './pages/tunnel/carpentry/carpentry-tunnel-products/carpentry-tunnel-products.component';
import { CarpentryTunnelInstallationComponent } from './pages/tunnel/carpentry/carpentry-tunnel-installation/carpentry-tunnel-installation.component';
import { CarpentryTunnelFundingComponent } from './pages/tunnel/carpentry/carpentry-tunnel-funding/carpentry-tunnel-funding.component';
import { CarpentryProductListComponent } from './pages/tunnel/carpentry/carpentry-product-list/carpentry-product-list.component';
import { CarpentryProductListModalComponent } from './modals/carpentry/carpentry-product-list-modal/carpentry-product-list-modal.component';
import { CarpentryTunnelValidationComponent } from './pages/tunnel/carpentry/carpentry-tunnel-validation/carpentry-tunnel-validation.component';
import { ScannerInputComponent } from './pages/scanner-input/scanner-input.component';
import { QuoteSentToCustomerComponent } from './pages/quote-sent-customer/quote-sent-to-customer.component';
import { CustomerInfoProjectsComponent } from './ui/customer-info/customer-info-projects/customer-info-projects.component';
import { AuthCallbackComponent } from './pages/auth-callback/auth-callback.component';
import {
  BathroomHbmTunnelFundingComponent
} from './pages/tunnel/bathroom-hbm/bathroom-hbm-tunnel-funding/bathroom-hbm-tunnel-funding.component';

registerLocaleData(localeFr);

@NgModule({
  declarations: [
    AppComponent,
    AuthCallbackComponent,
    KitchenComponent,
    KitchenModelComponent,
    KitchenLayoutComponent,
    KitchenOvenComponent,
    KitchenFridgeComponent,
    KitchenRecapComponent,
    KitchenStyleComponent,
    ModalsComponent,
    HomeComponent,
    HeaderComponent,
    CommunicationMediaGalleryComponent,
    CommunicationAlertDialogComponent,
    BtnComponent,
    CircleBtnComponent,
    BreadcrumbComponent,
    LoginComponent,
    RegisterComponent,
    InspirationTipComponent,
    ConfigurationComponent,
    Configurators2DComponent,
    Configurator2DSelectionComponent,
    ClosetDoorsStandardComponent,
    ClosetDoorsStandardPreviewComponent,
    ClosetDoorsStandardEnvironmentComponent,
    ClosetDoorsQuoteComponent,
    ClosetDoorsStandardDoorConfigurationComponent,
    ClosetDoorsStandardDoorRecessComponent,
    ClosetDoorsStandardDoorPlinthComponent,
    ClosetDoorsStandardDoorNumberComponent,
    ClosetDoorsStandardFinishesComponent,
    ClosetDoorsStandardFinishesDoorsComponent,
    ClosetDoorsStandardFinishesColorsComponent,
    ClosetDoorsStandardFinishesFramesComponent,
    ClosetDoorsStandardRangeComponent,
    ClosetDoorsStandardSummaryComponent,
    PreviewStepEnvironmentComponent,
    PreviewStepDoorConfigurationComponent,
    PreviewStepRangeComponent,
    PreviewStepFinishesComponent,
    PreviewStepSummaryComponent,
    ClosetDoorsPercentageComponent,
    ClosetDoorsStructureInfoComponent,
    ClosetDoorsPlinthInfoComponent,
    ClosetDoorsCustomComponent,
    ClosetDoorsCustomPreviewComponent,
    ClosetDoorsCustomEnvironmentComponent,
    ClosetDoorsCustomClosetConfigurationComponent,
    ClosetDoorsCustomSuppliersComponent,
    ClosetDoorsCustomFinishesComponent,
    ClosetDoorsCustomAccessoriesComponent,
    ClosetDoorsCustomSummaryComponent,
    ClosetDoorsAdministrationComponent,
    Configurators2DAdministrationComponent,
    FenceAdministrationComponent,
    DeckingBoardsAdministrationComponent,
    FenceComponent,
    FenceEnvironmentComponent,
    FenceConfigurationComponent,
    FenceAccessoriesComponent,
    FenceSummaryComponent,
    FenceInfosAngleKitComponent,
    FenceInfosBasePlateComponent,
    GenericInfoComponent,
    BaseAdministrationComponent,
    AdministratorsTabComponent,
    PricesTabComponent,
    PricesClosetDoorsCustomTabComponent,
    PicturesTabComponent,
    PicturesClosetDoorsCustomTabComponent,
    StatisticsTabComponent,
    FeatureSettingsTabComponent,
    FeatureSettingsAdministrationComponent,
    DiscountsTabComponent,
    CdmCarpentryTabComponent,
    DiscountCarpentryTabComponent,
    AccessoryCarpentryTabComponent,
    ClosetDoorsAdministrationMessageComponent,
    ProjectsComponent,
    PriceInfosComponent,
    ProjectComponent,
    ProductsListComponent,
    ProjectDuplicateComponent,
    ProjectDeleteComponent,
    DebugInfosComponent,
    OrderProductsComponent,
    InstallationQuoteComponent,
    Nl2BrPipe,
    SafeHtmlPipe,
    SafeStylePipe,
    ConvertDistancePipe,
    PrintMediasComponent,
    FidelityCardTypePipe,
    ImageZoomComponent,
    LogoutComponent,
    ImageLoaderComponent,
    ContactComponent,
    EditableTextComponent,
    StorageRoomComponent,
    StorageBoxComponent,
    StorageFrontComponent,
    StorageLegsComponent,
    StorageHandlesComponent,
    StorageRecapComponent,
    ModalMessageComponent,
    GenericErrorComponent,
    GenericErrorCustomerComponent,
    AdministrationAdministratorCreateComponent,
    AdministrationAdministratorUpdateComponent,
    AdministrationDiscountDeleteComponent,
    DeckingBoardsPreviewComponent,
    DeckingBoardsEnvironmentComponent,
    DeckingBoardsAccessoriesComponent,
    DeckingBoardsConfigurationComponent,
    DeckingBoardsFinishesComponent,
    DeckingBoardsSummaryComponent,
    DeckingBoardsComponent,
    DeckingBoardsClassInfoComponent,
    WorkbenchPlanGroupsComponent,
    WorkbenchPlanSectionsComponent,
    WorkbenchMaterialsColorsComponent,
    WorkbenchColorGroupsPlansComponent,
    WorkbenchFinishesComponent,
    WorkbenchJambsInfosComponent,
    WorkbenchPlanCuttingInfosComponent,
    WorkbenchSplashbackCuttingInfosComponent,
    WorkbenchSplashbacksComponent,
    WorkbenchSummaryComponent,
    WorkbenchComponent,
    CastoLazyImageDirective,
    CarpentryComponent,
    CarpentryConfigurationComponent,
    CarpentrySummaryComponent,
    ShoppingCartProductDeleteComponent,
    ShoppingCartComponent,
    ShoppingCartQuantityUpdateComponent,
    ShoppingCartInformationsComponent,
    ShoppingCartSupplierSendingComponent,
    ShoppingCartOrderNumberEditionComponent,
    ShoppingCartAddAssistanceComponent,
    ShoppingCartCheckOrderComponent,
    ShoppingCartVatUpdateComponent,
    ShoppingCartInstallationReminderComponent,
    CarpentryAccessoriesComponent,
    MultiVatQuoteComponent,
    QuotationDisabledProductsAlertComponent,
    AppointmentLinkComponent,
    SummaryComponent,
    FlexibleDatePickerComponent,
    IdentificationComponent,
    KitchenIdentificationComponent,
    SearchIdentificationComponent,
    FidelityInfoComponent,
    OfferCouponCardComponent,
    CardTypeChoiceComponent,
    CustomerTypeChoiceComponent,
    FidelityPaymentQRCodeComponent,
    KitchenValidationComponentv1,
    KitchenValidationProjectInfoComponent,
    FidelityCardValidationComponent,
    KitchenValidationProjectResumeComponent,
    KitchenValidationSideBarActionsComponent,
    KitchenValidationProjectResumeTableComponent,
    FidelityAdvantageComponent,
    ValidationProjectInfoComponent,
    VendorValidationComponent,
    ValidationProjectResumeComponent,
    ValidationProjectResumeTableComponent,
    ValidationSideBarActionsComponent,
    ButtonComponent,
    ActionBarComponent,
    CardMarketingComponent,
    SelectionCardComponent,
    TunnelComponent,
    TunnelBreadcrumbComponent,
    SearchBarComponent,
    BaseInstallationComponent,
    BaseFundingComponent,
    BaseValidationComponent,
    SafePipe,
    TutorialOverlayComponent,
    PortalPlaceholderComponent,
    CustomerInfoComponent,
    CustomerInfoProjectsComponent,
    CustomerInfoFidelityComponent,
    CustomerInfoDetailsComponent,
    ProjectInfoComponent,
    KitchenInstallationComponent,
    KitchenFundingComponent,
    KitchenTunnelValidationComponent,
    StorageComponent,
    StorageVendorInspirationComponent,
    StorageVendorBaseComponent,
    CollapsibleSectionComponent,
    MarketingConsentSelectorComponent,
    CollapsibleSectionComponent,
    StorageInstallationComponent,
    StorageInstallationComponent,
    BathroomHbmInstallationComponent,
    BathroomHbmTunnelValidationComponent,
    BathroomHbmTunnelFundingComponent,
    ModalWarningInspirationComponent,
    StorageFundingComponent,
    StorageTunnelValidationComponent,
    FreeCardCouponRewardsPipe,
    AppointmentComponent,
    PortalPlanningComponent,
    AppointmentIdentificationComponent,
    AppointmentCalendarComponent,
    CustomerInfoAppointmentComponent,
    HelpActionComponent,
    BaseComplementaryProductComponent,
    ComplementaryProductCardComponent,
    ComplementaryProductListComponent,
    ComplementaryProductFilterComponent,
    ComplementaryProductChipsComponent,
    ProductListComponent,
    ProductItemComponent,
    ActionButtonComponent,
    ProductDetailComponent,
    ProductDetailInfosSectionComponent,
    ProductDetailInfosListComponent,
    ProductDetailStarRatingComponent,
    PurchaseListComponent,
    PurchaseListItemComponent,
    QuantityInputComponent,
    EndingPageComponent,
    RegisterCustomerMobileComponent,
    UpdateCustomerMobileComponent,
    SearchCustomerMobileComponent,
    IdentificationResultCardListComponent,
    IdentificationResultCardListItemComponent,
    GenericConfirmComponent,
    BasketConfirmationComponent,
    CustomerInfoFormComponent,
    CiamProfileCompletionComponent,
    ConfigSentScreenComponent,
    LightBoxCarouselComponent,
    BathroomTunnelProductsComponent,
    BasketTunnelProductsComponent,
    TunnelProductSearchComponent,
    TunnelProductListComponent,
    TunnelProductCardComponent,
    TunnelCartPanelComponent,
    TunnelCartListComponent,
    TunnelCartItemComponent,
    BasketTunnelValidationComponent,
    BathroomTunnelInstallationComponent,
    BathroomTunnelFundingComponent,
    BathroomTunnelConceptionComponent,
    BathroomTunnelConceptionSummaryComponent,
    BathroomConfigurationPreviewComponent,
    DesktopConfiguratorTemplateComponent,
    BathroomConfiguratorChoiceComponent,
    BathroomRangeSelectorComponent,
    CompositionComponent,
    CompositionFiltersComponent,
    BathroomTunnelValidationComponent,
    BathroomConfigurationComponent,
    BathroomConfigurationEndingComponent,
    BathroomRenderComponent,
    CarpentryProductListComponent,
    CarpentryTunnelProductsComponent,
    CarpentryTunnelInstallationComponent,
    CarpentryTunnelFundingComponent,
    CarpentryProductListModalComponent,
    CarpentryTunnelValidationComponent,
    QuoteSentToCustomerComponent,
    FidelityVoucherPublicAccessComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    CommonModule,
    WorkbenchPreviewModule,
    ErrorPanelComponent,
    AppRoutingModule,
    FormsModule,
    SliderModule,
    SkeletonModule,
    InputSwitchModule,
    RadioButtonModule,
    InputNumberModule,
    ReactiveFormsModule,
    SelectButtonModule,
    ReactiveFormsModule,
    DropdownModule,
    ToggleButtonModule,
    CarouselModule,
    MessagesModule,
    CalendarModule,
    AccordionModule,
    ToastModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    MessagesModule,
    TooltipModule,
    TriStateCheckboxModule,
    ConfirmDialogModule,
    TableModule,
    FullCalendarModule,
    PanelModule,
    CalendarModule,
    DialogModule,
    ConfirmDialogModule,
    SidebarModule,
    ToastModule,
    BrowserAnimationsModule,
    NgSelectModule,
    FontAwesomeModule,
    NgxDatatableModule,
    InputGroupModule,
    InputGroupAddonModule,
    AlertModule,
    FullCalendarModule,
    NgxJsonViewerModule,
    FloatLabelModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoader,
        deps: [HttpClient],
      },
    }),
    BreadcrumbModule,
    SplitButtonModule,
    TabMenuModule,
    TreeModule,
    ChipModule,
    FencePreviewModule,
    FenceMaterialsModule,
    LoaderComponent,
    TooltipDirective,
    FeatureDiscoveryDirective,
    PaginatorModule,
    ScannerInputComponent,
    QRCodeModule,
  ],
  providers: [
    DatasService,
    GTM,
    CurrentService,
    ModalService,
    HbmIframeService,
    HbmApiService,

    ToastService,
    MessageService,
    ServiceManager,
    ClosetDoorsStandardComponent,
    configuratorProviders,
    closetDoorsCustomProviders,
    fenceProviders,
    deckingBoardsProviders,
    kitchensProviders,
    storagesProviders,
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [DatasService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: customerMaintenanceInitializer,
      deps: [FeatureSettingsService, Router],
      multi: true,
    },
    provideHttpClient(withInterceptors([globalErrorInterceptor])),
    {
      provide: ErrorHandler,
      useFactory: () => {
        if (environment.sentry.dsn?.length > 0) {
          return Sentry.createErrorHandler();
        }
        return new GlobalErrorHandler();
      },
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    BathroomConfigurationService,
    BathroomValidationService,
    BathroomEndingService,
    bathroomProviders,
    basketProviders,
    bathroomHbmProviders,
    { provide: UNSAVED_CHANGES_CHECKERS, useExisting: BathroomConfigurationService, multi: true },
    { provide: LOCALE_ID, useValue: 'fr-FR' },
  ],
})
export class AppModule {}

export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/locales/', '.json?v=' + environment.version);
}

export function initApp(datas: DatasService) {
  return datas.init;
}
